<h2 mat-dialog-title>
  <mat-icon>call_split</mat-icon>
  Voice &rarr; Text Fallback
</h2>

<div mat-dialog-content class="dialog-content">
  <p class="customer-id">
    Customer: <code>{{ data.customerId }}</code>
    <span *ngIf="data.planNumber"> &middot; Plan {{ data.planNumber }}</span>
  </p>

  <div
    class="session-banner"
    *ngIf="data.voiceSessionId && hasSearchedOnce() && !isLoading()"
    [class.found]="currentSessionFound()"
  >
    <mat-icon>{{
      currentSessionFound() ? 'my_location' : 'help_outline'
    }}</mat-icon>
    <p *ngIf="currentSessionFound()">
      Showing only the voice session you opened this from.
    </p>
    <p *ngIf="!currentSessionFound()">
      Couldn't find that exact session — showing all
      {{ traces().length }} voice session(s) for this customer instead.
    </p>
  </div>

  <div class="controls">
    <mat-form-field appearance="outline">
      <mat-label>Fallback Window</mat-label>
      <mat-select
        [(ngModel)]="fallbackWindowMs"
        (selectionChange)="fetchTraces()"
      >
        <mat-option
          *ngFor="let option of fallbackWindowOptions"
          [value]="option.value"
        >
          {{ option.label }}
        </mat-option>
      </mat-select>
    </mat-form-field>
    <button mat-stroked-button (click)="fetchTraces()" [disabled]="isLoading()">
      <mat-icon>refresh</mat-icon>
      Refresh
    </button>
  </div>

  <div class="loading" *ngIf="isLoading()">
    <mat-spinner diameter="36"></mat-spinner>
    <p>Tracing voice &rarr; text fallback...</p>
  </div>

  <div class="error" *ngIf="errorMessage() && !isLoading()">
    <mat-icon>error_outline</mat-icon>
    <p>{{ errorMessage() }}</p>
  </div>

  <ng-container *ngIf="!isLoading() && !errorMessage()">
    <div class="summary-grid" *ngIf="hasSearchedOnce()">
      <div class="summary-card">
        <div class="summary-label">Voice Sessions</div>
        <div class="summary-value">{{ totalVoiceSessions() }}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Fell Back</div>
        <div class="summary-value">{{ totalFallbacks() }}</div>
      </div>
      <div class="summary-card highlight">
        <div class="summary-label">Fallback Rate</div>
        <div class="summary-value">{{ fallbackRate() }}</div>
      </div>
    </div>

    <div class="table-container" *ngIf="traces().length > 0">
      <table
        mat-table
        [dataSource]="traces()"
        class="traces-table"
        multiTemplateDataRows
      >
        <ng-container matColumnDef="voiceSessionId">
          <th mat-header-cell *matHeaderCellDef>Voice Session ID</th>
          <td mat-cell *matCellDef="let element">
            <span
              class="cell-value"
              [matTooltip]="element.voiceSessionId"
              matTooltipPosition="above"
            >
              {{ element.voiceSessionId | slice: 0 : 12 }}...
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="voiceStart">
          <th mat-header-cell *matHeaderCellDef>Voice Start</th>
          <td mat-cell *matCellDef="let element">
            {{ formatDate(element.voiceSessionStart) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="duration">
          <th mat-header-cell *matHeaderCellDef>Voice Duration</th>
          <td mat-cell *matCellDef="let element">
            {{ formatDuration(element.voiceSessionDuration) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="keepalives">
          <th mat-header-cell *matHeaderCellDef>Keepalives</th>
          <td mat-cell *matCellDef="let element">
            {{ element.voiceSessionKeepalives }}
          </td>
        </ng-container>

        <ng-container matColumnDef="voiceMessages">
          <th mat-header-cell *matHeaderCellDef>Voice Messages</th>
          <td mat-cell *matCellDef="let element">
            {{ element.voiceMessageCount }}
          </td>
        </ng-container>

        <ng-container matColumnDef="fallback">
          <th mat-header-cell *matHeaderCellDef>Fell Back?</th>
          <td mat-cell *matCellDef="let element">
            <span
              class="badge"
              [ngClass]="
                element.fallbackOccurred ? 'badge-warn' : 'badge-success'
              "
            >
              {{ element.fallbackOccurred ? 'Yes' : 'No' }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="matchType">
          <th mat-header-cell *matHeaderCellDef>Match Type</th>
          <td mat-cell *matCellDef="let element">
            <span
              *ngIf="element.fallbackOccurred"
              class="badge"
              [ngClass]="
                element.idConfirmed ? 'badge-success' : 'badge-neutral'
              "
              [matTooltip]="
                element.fallbackReason ||
                (element.idConfirmed
                  ? 'Confirmed: the text conversation explicitly continues this voice session ID'
                  : 'Unconfirmed: text conversation only started within the fallback window, not matched by session ID')
              "
            >
              {{ element.idConfirmed ? 'ID-Confirmed' : 'Time-Window Only' }}
            </span>
            <span *ngIf="!element.fallbackOccurred">—</span>
          </td>
        </ng-container>

        <ng-container matColumnDef="timeToFallback">
          <th mat-header-cell *matHeaderCellDef>Time to Fallback</th>
          <td mat-cell *matCellDef="let element">
            {{
              element.timeSinceFallbackMs != null
                ? formatDuration(element.timeSinceFallbackMs)
                : '—'
            }}
          </td>
        </ng-container>

        <ng-container matColumnDef="textMessages">
          <th mat-header-cell *matHeaderCellDef>Text Messages</th>
          <td mat-cell *matCellDef="let element">
            <button
              *ngIf="element.textMessages?.length; else plainCount"
              class="expand-toggle"
              type="button"
              (click)="toggleExpanded(element)"
            >
              {{ element.textMessageCount ?? element.textMessages.length }}
              <mat-icon>{{
                expandedSessionId() === element.voiceSessionId
                  ? 'expand_less'
                  : 'expand_more'
              }}</mat-icon>
            </button>
            <ng-template #plainCount>{{
              element.textMessageCount ?? '—'
            }}</ng-template>
          </td>
        </ng-container>

        <ng-container matColumnDef="expandedDetail">
          <td
            mat-cell
            *matCellDef="let element"
            [attr.colspan]="displayedColumns.length"
          >
            <div
              class="text-messages-detail"
              *ngIf="expandedSessionId() === element.voiceSessionId"
            >
              <div
                class="text-message"
                *ngFor="let msg of element.textMessages"
                [ngClass]="msg.role"
              >
                <span class="text-message-timestamp"
                  >[{{ msg.timestamp | date: 'shortTime' }}]</span
                >
                <span class="text-message-role"
                  >{{ msg.role | titlecase }}:</span
                >
                <span class="text-message-content">{{ msg.message }}</span>
              </div>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: ['expandedDetail']"
          class="detail-row"
          [class.expanded]="expandedSessionId() === row.voiceSessionId"
        ></tr>
      </table>
    </div>

    <div class="empty" *ngIf="traces().length === 0 && hasSearchedOnce()">
      <mat-icon>search_off</mat-icon>
      <p>No voice sessions found for this customer.</p>
    </div>
  </ng-container>
</div>

<div mat-dialog-actions align="end">
  <button mat-button (click)="dialogRef.close()">Close</button>
</div>
